// frontend/packages/business-console/src/lib/customerCache.js
//
// Work item 5.9 — IndexedDB cache of last-viewed customers, so the
// CRM detail page degrades gracefully in dead zones. Design source:
// roadmap §11 / D18 ("Console offline = PWA + IndexedDB outbox +
// photo upload queue; full offline CRM is out of scope" — this is
// the bounded read-only fallback).
//
// Schema (db `upryzze-console`, store `customerCache`):
//
//   {
//     customerId:  string,       // primary key
//     snapshot:    object,       // shape mirrors crmService.getCustomer
//                                // response: { customer, activities,
//                                // linkedCustomers, person }
//     submissions: object[],     // 4.11b list-shape rows (no values,
//                                // no attachments — those are PII-
//                                // heavy and we already omit them
//                                // server-side)
//     cachedAt:    number,       // ms epoch
//   }
//
// Lives in the SAME IndexedDB database as the 2.19 outbox store
// (`upryzze-console`) — distinct object stores so writes don't
// collide.
//
// Per D8 — the cache holds plaintext PII (the API already returns
// decrypted PII to operator-side callers). The mitigation per the
// roadmap is "wipe on logout + 24h TTL", which `clearAllCustomerCache`
// and `getCachedCustomer`'s TTL check enforce. Browser IndexedDB is
// origin-scoped; encrypting the values would not meaningfully
// improve security given an attacker with the operator's session.
//
// No npm dep — same native IndexedDB / Promise-wrapped pattern as
// `outboxStore.js`.

const DB_NAME = 'upryzze-console';
const DB_VERSION = 2; // 2.19 used v1; we bump to add `customerCache`
const OUTBOX_STORE = 'outbox';
const CUSTOMER_STORE = 'customerCache';

// 24 hours per the roadmap brief.
export const CUSTOMER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB not available'));
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      // Co-create the 2.19 outbox store if it doesn't exist (covers
      // fresh installs that hit 5.9's bumped DB version first).
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const outboxStore = db.createObjectStore(OUTBOX_STORE, {
          keyPath: 'clientUuid',
        });
        outboxStore.createIndex('by_kind_status', ['kind', 'status']);
        outboxStore.createIndex('by_kind', 'kind');
        outboxStore.createIndex('by_savedAt', 'savedAt');
      }
      if (!db.objectStoreNames.contains(CUSTOMER_STORE)) {
        const cs = db.createObjectStore(CUSTOMER_STORE, {
          keyPath: 'customerId',
        });
        cs.createIndex('by_cachedAt', 'cachedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(db, mode) {
  return db.transaction(CUSTOMER_STORE, mode).objectStore(CUSTOMER_STORE);
}

function wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Returns the cached snapshot if it exists AND is younger than the
 * TTL. Returns `null` otherwise (no row, or stale). Stale entries
 * are NOT auto-deleted on read — `clearAllCustomerCache` (called on
 * logout) and the read-side TTL check together keep the data
 * windowed.
 */
export async function getCachedCustomer(customerId) {
  if (!customerId) return null;
  try {
    const db = await openDb();
    const row = await wrap(tx(db, 'readonly').get(String(customerId)));
    if (!row) return null;
    if (
      typeof row.cachedAt !== 'number' ||
      Date.now() - row.cachedAt > CUSTOMER_CACHE_TTL_MS
    ) {
      return null;
    }
    return row;
  } catch {
    return null;
  }
}

/**
 * Write a fresh snapshot. Callers pass the shape from
 * `getCustomer(id)` (the API response) plus the optional recent
 * submissions list. `cachedAt` is stamped here so callers don't
 * have to. Errors are swallowed — caching is best-effort, never
 * blocks the actual UX.
 */
export async function putCachedCustomer(customerId, snapshot, submissions) {
  if (!customerId || !snapshot) return;
  try {
    const db = await openDb();
    await wrap(
      tx(db, 'readwrite').put({
        customerId: String(customerId),
        snapshot,
        submissions: Array.isArray(submissions) ? submissions : [],
        cachedAt: Date.now(),
      })
    );
  } catch {
    /* swallow */
  }
}

/**
 * Wipe the entire cache. Wired into the auth `logout` flow so PII
 * doesn't survive a session change on a shared device.
 */
export async function clearAllCustomerCache() {
  try {
    const db = await openDb();
    await wrap(tx(db, 'readwrite').clear());
  } catch {
    /* swallow */
  }
}

// Test-only escape hatch — same shape as outboxStore's.
export async function __resetForTests() {
  try {
    if (dbPromise) {
      const db = await dbPromise;
      db.close();
    }
  } catch {
    /* ignore */
  }
  dbPromise = null;
}
